/**
 * Phase 8 movement techniques: crouch and slide, bunny-hop landing grace,
 * wall-running, and wall-jumps (including bounces off a wall hit at speed).
 * Air strafing lives in Locomotion.air; surfing needs no code of its own (it
 * is air strafing against a steep face, which the resolver slides along
 * without friction).
 *
 * Everything here is deterministic simulation code: it reads the tick's
 * intent and the collision world, and keeps its state on PlayerState so state
 * hashes and replays cover it. See MOVEMENT_SPEC.md, "Techniques".
 */
import { Vec3 } from '../core/Vec3';
import type { EngineConfig, MovementConfig, PlayerShapeConfig } from '../core/Config';
import { ProximityContact, type CapsuleShape } from '../simulation/CollisionQueries';
import type { ContactResolver, MotionResult } from './ContactResolver';
import { turnRate } from './Locomotion';
import type { MovementIntent } from './MovementRules';
import type { PlayerState } from './PlayerState';

/** Two wall contacts are the same wall when their normals agree to this cosine and their planes to SAME_PLANE metres. */
const SAME_WALL = 0.95;
const SAME_PLANE = 0.35;
/** A wall touched within this many ticks is remembered as the same wall (wall-run continuity). */
const WALL_MEMORY_TICKS = 10;
/** A wall-run needs the velocity to run along the wall: at most this much of the planar speed may point into or away from it. */
const WALL_RUN_MAX_INTO = 0.75;
/** Input pointing away from the wall by more than this (wish·n) ends a wall-run. */
const WALL_RUN_RELEASE = 0.5;

export class Techniques {
  private readonly cfg: Readonly<MovementConfig>;
  private readonly player: Readonly<PlayerShapeConfig>;
  private readonly standHalf: number;
  private readonly crouchHalf: number;
  private readonly near: ProximityContact[] = [];
  private readonly planar = new Vec3();
  private readonly wish = new Vec3();
  private readonly tmp = new Vec3();
  private readonly slope = new Vec3();

  constructor(
    config: Readonly<EngineConfig>,
    private readonly shape: CapsuleShape,
    private readonly resolver: ContactResolver,
  ) {
    this.cfg = config.movement;
    this.player = config.player;
    const r = config.player.radius;
    this.standHalf = Math.max(0, config.player.height / 2 - r);
    this.crouchHalf = Math.max(0, config.player.crouchHeight / 2 - r);
  }

  /** Eye height above the feet for the current posture. */
  eyeHeight(state: PlayerState): number {
    return state.crouched ? this.player.crouchEyeHeight : this.player.eyeHeight;
  }

  /** Matches the collision shape to the posture (after a respawn or a rebuilt simulation). */
  syncShape(state: PlayerState): void {
    this.shape.halfSegment = state.crouched ? this.crouchHalf : this.standHalf;
  }

  /**
   * Start of tick: posture, then slide start. Crouching on the ground keeps
   * the feet where they are; in the air it pulls the feet up (the head stays),
   * so a crouch jump clears higher ledges. Standing up needs room.
   */
  begin(state: PlayerState, crouchHeld: boolean): void {
    const up = state.up;
    const pressed = crouchHeld && !state.crouchHeldPrev;
    state.crouchHeldPrev = crouchHeld;
    if (state.slideCooldown > 0) state.slideCooldown--;
    const delta = this.standHalf - this.crouchHalf;
    if (crouchHeld && !state.crouched) {
      state.crouched = true;
      if (!state.grounded) {
        state.position.addScaled(up, 2 * delta);
        state.previousPosition.addScaled(up, 2 * delta);
      }
    } else if (!crouchHeld && state.crouched) {
      if (state.grounded) {
        if (this.resolver.fits(state.position, up, this.standHalf)) state.crouched = false;
      } else {
        // Lower the feet back (the head stays) if there is room below; otherwise stand up from the feet.
        const lowered = this.tmp.copy(state.position).addScaled(up, -2 * delta);
        if (this.resolver.fits(lowered, up, this.standHalf)) {
          state.position.copy(lowered);
          state.previousPosition.addScaled(up, -2 * delta);
          state.crouched = false;
        } else if (this.resolver.fits(state.position, up, this.standHalf)) {
          state.crouched = false;
        }
      }
    }
    this.syncShape(state);
    if (!state.crouched || !state.grounded) state.sliding = false;

    // A slide starts when crouch is pressed while moving fast on the ground, or on landing with crouch held.
    if (state.grounded && state.crouched && !state.sliding && (pressed || state.groundedTicks === 1)) {
      const v = state.velocity;
      const s = planarSpeed(v, up);
      if (s >= this.cfg.slideMinSpeed) {
        state.sliding = true;
        // The boost tops speed up to at most runSpeed + slideBoost, so hop-sliding cannot stack it.
        const boost = Math.min(this.cfg.slideBoost, this.cfg.runSpeed + this.cfg.slideBoost - s);
        if (state.slideCooldown === 0 && boost > 0) {
          // The boost is along the planar motion only.
          const k = boost / s;
          const vu = v.dot(up);
          v.addScaled(up, -vu).scale(1 + k).addScaled(up, vu);
          state.slideCooldown = this.cfg.slideBoostCooldownTicks;
        }
      }
    }
  }

  /** True while landing grace suspends ground friction (a jump now keeps every bit of speed). */
  inLandingGrace(state: PlayerState): boolean {
    return state.groundedTicks <= this.cfg.bhopGraceTicks && planarSpeed(state.velocity, state.up) > this.cfg.runSpeed;
  }

  /**
   * Slide locomotion: low friction, weak steering, full slope gravity (a slide
   * down a ramp accelerates, a slide up one slows quickly).
   */
  slide(v: Vec3, intent: MovementIntent, normal: Readonly<Vec3>, up: Readonly<Vec3>, gravity: number, dt: number): void {
    const cfg = this.cfg;
    v.projectOnPlane(normal);
    let s = v.length();
    if (intent.wishStrength > 0 && s > 1e-9) {
      const wish = this.wish.copy(intent.wishDir).projectOnPlane(normal);
      if (wish.lengthSq() > 1e-12) {
        wish.normalize();
        if (v.dot(wish) / s > -0.5) v.rotateToward(wish, turnRate(cfg.slideTurnRate, s, cfg.turnSpeedRef) * dt, this.tmp);
      }
    }
    s = v.length();
    if (s > 1e-9) {
      const next = Math.max(0, s - cfg.slideDecel * dt) * Math.exp(-cfg.slideDecayRate * dt);
      v.scale(next / s);
    }
    const slope = this.slope.copy(up).scale(-gravity).projectOnPlane(normal);
    v.addScaled(slope, cfg.slideSlopeGain * dt);
  }

  /** Ends a slide that has slowed to a crawl (it becomes a crouch walk). */
  settleSlide(state: PlayerState): void {
    if (state.sliding && planarSpeed(state.velocity, state.up) < this.cfg.slideEndSpeed) state.sliding = false;
  }

  /**
   * Wall-run locomotion (in place of the air model): the velocity follows the
   * wall, gravity is scaled down, speed decays slowly. Returns the gravity
   * multiplier for this tick.
   */
  wallRun(state: PlayerState, v: Vec3, dt: number): number {
    const cfg = this.cfg;
    const n = state.wallNormal;
    const up = state.up;
    const vu = v.dot(up);
    const p = this.planar.copy(v).addScaled(up, -vu);
    const s = p.length();
    // Follow the wall: the planar velocity is turned into the wall's plane at the same speed.
    p.addScaled(n, -p.dot(n));
    const along = p.length();
    if (along > 1e-9) p.scale((s * Math.exp(-cfg.wallRunDecayRate * dt)) / along);
    v.copy(p).addScaled(up, vu);
    // A small pull toward the wall keeps it within reach on gentle curves.
    v.addScaled(n, -0.5 * dt);
    state.wallRunTicks++;
    // Only the fall is slowed: rising along a wall takes full gravity, so a wall-run never adds height.
    return vu > 0 ? 1 : cfg.wallRunGravityScale;
  }

  /**
   * Wall-jump, if one is available: while wall-running, or within
   * wallCoyoteTicks of touching a wall in the air, and not from the wall last
   * used (no climbing one wall), and with less lift each time since the ground. Pushes off at the larger of wallJumpOut and
   * restitution × the speed the player came into the wall with, so a jump
   * timed at the impact is a bounce.
   */
  wallJump(state: PlayerState, v: Vec3): boolean {
    const cfg = this.cfg;
    if (state.grounded || state.ticksSinceWall >= cfg.wallCoyoteTicks) return false;
    const n = state.wallNormal;
    if (isUsedWall(state, n, state.wallOffset)) return false;
    const up = state.up;
    const into = v.dot(n);
    if (into < 0) v.addScaled(n, -into);
    const out = Math.max(cfg.wallJumpOut, cfg.wallBounceRestitution * state.wallApproachSpeed);
    v.addScaled(n, out);
    // Upward speed becomes at least this jump's (it does not stack on a rise already under way), and
    // each wall-jump since the ground gives less: a chain climbs at most sum(up_k^2) / 2g.
    const lift = cfg.wallJumpUp * cfg.wallJumpChainDecay ** state.wallJumps;
    const vu = v.dot(up);
    if (vu < lift) v.addScaled(up, lift - vu);
    state.wallJumps++;
    this.useWall(state);
    state.wallRunTicks = 0;
    state.ticksSinceWall = 1_000_000;
    state.wallApproachSpeed = 0;
    return true;
  }

  /**
   * End of tick: ground bookkeeping, then what walls are within reach, wall
   * impacts this tick, and wall-run start and end.
   */
  sense(state: PlayerState, intent: MovementIntent, motion: MotionResult, jumped: boolean): void {
    const cfg = this.cfg;
    const up = state.up;
    if (state.grounded) {
      state.groundedTicks++;
      state.wallRunTicks = 0;
      state.usedWall = false;
      state.wallJumps = 0;
      state.ticksSinceWall = 1_000_000;
      state.wallApproachSpeed = 0;
      this.settleSlide(state);
      return;
    }
    state.groundedTicks = 0;
    state.sliding = false;
    if (state.ticksSinceWall < 1_000_000) state.ticksSinceWall++;

    // Walls hit this tick (the slide already removed the speed into them).
    let hitSpeed = 0;
    for (let i = 0; i < motion.hitCount; i++) {
      const h = motion.hits[i]!;
      if (h.material?.kind === 'lethal' || Math.abs(h.normal.dot(up)) > cfg.wallMaxNormalUp) continue;
      hitSpeed = Math.max(hitSpeed, h.impactSpeed);
    }

    let lost = state.wallRunTicks > 0;
    // The nearest wall within reach.
    const count = this.resolver.nearby(state.position, up, cfg.wallReach, this.near);
    let best: ProximityContact | null = null;
    for (let i = 0; i < count; i++) {
      const c = this.near[i]!;
      if (c.material?.kind === 'lethal' || Math.abs(c.normal.dot(up)) > cfg.wallMaxNormalUp) continue;
      if (!best || c.distance < best.distance) best = c;
    }
    if (best) {
      const n = this.tmp.copy(best.normal).addScaled(up, -best.normal.dot(up)).normalize();
      const offset = n.dot(best.point);
      const sameWall = state.ticksSinceWall <= WALL_MEMORY_TICKS && samePlane(state.wallNormal, state.wallOffset, n, offset);
      lost = state.wallRunTicks > 0 && !sameWall;
      const approach = Math.max(hitSpeed, -state.velocity.dot(n));
      state.wallApproachSpeed = sameWall ? Math.max(state.wallApproachSpeed, approach) : approach;
      state.wallNormal.copy(n);
      state.wallOffset = offset;
      state.ticksSinceWall = 0;
      // Touching a different wall frees the one last used.
      if (state.usedWall && !isUsedWall(state, n, offset)) state.usedWall = false;
    }

    // Wall-run end and start.
    if (state.wallRunTicks > 0) {
      const slow = planarSpeed(state.velocity, up) < cfg.wallRunMinSpeed * 0.8;
      const released = intent.wishStrength <= 0 || intent.wishDir.dot(state.wallNormal) > WALL_RUN_RELEASE;
      if (lost || slow || released || jumped || state.wallRunTicks >= cfg.wallRunMaxTicks) {
        // Running onto another wall (an inside corner) ends the run without using either: the wall
        // just touched frees the old one, and the new one was never run on.
        if (!(lost && best)) this.useWall(state);
        state.wallRunTicks = 0;
      }
    } else if (best && !jumped && this.canStartWallRun(state, intent)) {
      state.wallRunTicks = 1;
      const v = state.velocity;
      const vu = v.dot(up);
      // Catch the fall: a wall-run starts level or rising.
      if (vu < 0) v.addScaled(up, -vu);
    }
  }

  private canStartWallRun(state: PlayerState, intent: MovementIntent): boolean {
    const cfg = this.cfg;
    const up = state.up;
    const n = state.wallNormal;
    if (isUsedWall(state, n, state.wallOffset)) return false;
    if (intent.wishStrength <= 0 || intent.wishDir.dot(n) > 0.25) return false;
    const v = state.velocity;
    if (v.dot(up) < -cfg.wallRunMaxEntryFall) return false;
    const p = this.planar.copy(v).addScaled(up, -v.dot(up));
    const s = p.length();
    if (s < cfg.wallRunMinSpeed) return false;
    return Math.abs(p.dot(n)) / s <= WALL_RUN_MAX_INTO;
  }

  private useWall(state: PlayerState): void {
    state.usedWall = true;
    state.usedWallNormal.copy(state.wallNormal);
    state.usedWallOffset = state.wallOffset;
  }
}

function samePlane(n1: Readonly<Vec3>, d1: number, n2: Readonly<Vec3>, d2: number): boolean {
  return n1.dot(n2) > SAME_WALL && Math.abs(d1 - d2) < SAME_PLANE;
}

function isUsedWall(state: PlayerState, n: Readonly<Vec3>, offset: number): boolean {
  return state.usedWall && samePlane(state.usedWallNormal, state.usedWallOffset, n, offset);
}

export function planarSpeed(v: Readonly<Vec3>, up: Readonly<Vec3>): number {
  const vu = v.dot(up);
  return Math.sqrt(Math.max(0, v.lengthSq() - vu * vu));
}
